import { useCallback } from 'react';
import { useApi, usePaginatedApi, useApiWithParams } from './useApi';
import { articleService, ArticleFilters, ArticleSearchParams } from '../services/articleService';
import { Article } from '../config/api';

// Hook for paginated articles list
export function useArticles(filters: ArticleFilters = {}) {
  const apiCall = useCallback(
    (page: number, limit: number) => 
      articleService.getArticles({ ...filters, page, limit }),
    [filters]
  );

  return usePaginatedApi(apiCall, filters.page || 1, filters.limit || 10);
}

// Hook for single article
export function useArticle(slug: string) {
  const apiCall = useCallback(
    () => articleService.getArticle(slug),
    [slug]
  );

  const { data, loading, error, refetch } = useApi(apiCall, {
    immediate: !!slug,
    onSuccess: () => {
      // Track article view
      if (slug) {
        articleService.trackView(slug);
      }
    },
  });

  return {
    article: data || null,
    loading,
    error,
    refetch,
  };
}

// Hook for featured articles
export function useFeaturedArticles(limit: number = 5) {
  const apiCall = useCallback(
    () => articleService.getFeaturedArticles(limit),
    [limit]
  );

  const { data, loading, error, refetch } = useApi(apiCall);

  return {
    articles: data?.articles || [],
    loading,
    error,
    refetch,
  };
}

// Hook for recent articles
export function useRecentArticles(limit: number = 10) {
  const apiCall = useCallback(
    () => articleService.getRecentArticles(limit),
    [limit]
  );

  const { data, loading, error, refetch } = useApi(apiCall);

  return {
    articles: data?.articles || [],
    loading,
    error,
    refetch,
  };
}

// Hook for article search
export function useArticleSearch() {
  const { data, loading, error, execute } = useApiWithParams(
    (params: ArticleSearchParams) => articleService.searchArticles(params),
    { immediate: false }
  );

  const search = useCallback((term: string, filters: Omit<ArticleSearchParams, 'term'> = {}) => {
    if (term.trim()) {
      execute({ term, ...filters });
    }
  }, [execute]);

  return {
    results: data?.data || [],
    pagination: data?.pagination || null,
    loading,
    error,
    search,
  };
}

// Hook for articles by tag
export function useArticlesByTag(tag: string, page: number = 1, limit: number = 10) {
  const apiCall = useCallback(
    () => articleService.getArticlesByTag(tag, page, limit),
    [tag, page, limit]
  );

  const { data, loading, error, refetch } = useApi(apiCall, {
    immediate: !!tag,
  });

  return {
    articles: data?.data || [],
    pagination: data?.pagination || null,
    loading,
    error,
    refetch,
  };
}

// Hook for articles by date range
export function useArticlesByDateRange(dateFrom: string, dateTo: string, page: number = 1) {
  const apiCall = useCallback(
    () => articleService.getArticlesByDateRange(dateFrom, dateTo, page),
    [dateFrom, dateTo, page]
  );

  const { data, loading, error, refetch } = useApi(apiCall, {
    immediate: !!(dateFrom && dateTo),
  });

  return {
    articles: data?.data || [],
    pagination: data?.pagination || null,
    loading,
    error,
    refetch,
  };
}

// Hook for article analytics tracking
export function useArticleTracking() {
  const trackView = useCallback(async (slug: string) => {
    try {
      await articleService.trackView(slug);
    } catch (error) {
      console.warn('Failed to track article view:', error);
    }
  }, []);

  const trackShare = useCallback(async (slug: string, platform: string) => {
    try {
      await articleService.trackShare(slug, platform);
    } catch (error) {
      console.warn('Failed to track article share:', error);
    }
  }, []);

  const trackClick = useCallback(async (slug: string) => {
    try {
      await articleService.trackClick(slug);
    } catch (error) {
      console.warn('Failed to track article click:', error);
    }
  }, []);

  return {
    trackView,
    trackShare,
    trackClick,
  };
}

// Hook for article management (admin)
export function useArticleManagement() {
  const { execute: createArticle, loading: creating, error: createError } = useApiWithParams(
    (articleData: Partial<Article>) => articleService.createArticle(articleData),
    { immediate: false }
  );

  const { execute: updateArticle, loading: updating, error: updateError } = useApiWithParams(
    (id: number, articleData: Partial<Article>) => articleService.updateArticle(id, articleData),
    { immediate: false }
  );

  const { execute: deleteArticle, loading: deleting, error: deleteError } = useApiWithParams(
    (id: number) => articleService.deleteArticle(id),
    { immediate: false }
  );

  return {
    createArticle,
    updateArticle,
    deleteArticle,
    loading: creating || updating || deleting,
    error: createError || updateError || deleteError,
  };
}
